import React from "react";
import { IKO } from "./tokens";

/**
 * Vania Msg 214 (30 Sep 2026) — shared iko-mix visual for organiser, venue and
 * member cards + profiles. ONE component, three surfaces. Two elements:
 *   · a thin stacked bar in iko colours (proportion of events per iko)
 *   · chips of the top 1-2 ikos in plain English, e.g. "mostly Culture / Arts"
 *
 * Rules:
 *   · With fewer than `barMinEvents` events (default 3), show chips only, no bar.
 *   · `overrideChips` from admin desk takes priority — the auto bar still renders.
 *   · Same colours as IKO tokens; never invents. Silently renders nothing if
 *     the supplied `counts` object is empty and no override is set.
 *
 * `counts` shape: { sherehe: 3, polite: 5, michezo: 1, kazi: 0 }
 */
export function IkoMix({
  counts = {},
  overrideChips = null,       // string[] | null — admin override, wins for chip labels
  total = null,               // optional; falls back to sum(counts)
  barMinEvents = 3,
  label = "usually runs",     // prefix for chip line ("usually runs Culture / Arts")
  compact = false,            // smaller variant for card use
  testId = "iko-mix",
}) {
  const entries = Object.entries(counts || {}).filter(([, n]) => (n || 0) > 0);
  const sum = total != null ? total : entries.reduce((a, [, n]) => a + n, 0);
  const ranked = entries.sort((a, b) => (b[1] || 0) - (a[1] || 0));

  const showBar = sum >= barMinEvents && ranked.length > 0;
  const chipKeys = overrideChips && overrideChips.length
    ? overrideChips
    : ranked.slice(0, 2).map(([k]) => k);

  if (!chipKeys.length && !showBar) return null;

  return (
    <div data-testid={testId} style={{ display: "flex", flexDirection: "column",
                                       gap: compact ? 4 : 6, width: "100%" }}>
      {showBar && (
        <div aria-hidden style={{ display: "flex", width: "100%",
                                  height: compact ? 4 : 6, borderRadius: 999,
                                  overflow: "hidden", background: "var(--ww-line)" }}>
          {ranked.map(([k, n]) => {
            const pct = Math.max(0, Math.min(100, (n / sum) * 100));
            const c = IKO[k]?.bright || "var(--ww-line)";
            return (
              <span key={k} style={{ width: `${pct}%`, background: c, height: "100%" }} />
            );
          })}
        </div>
      )}
      {chipKeys.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          {chipKeys.length === 1 && (
            <span style={{ fontFamily: "Arial", fontSize: compact ? 10 : 11, color: "var(--ww-text-muted)",
                           textTransform: "lowercase" }}>
              {label}
            </span>
          )}
          {chipKeys.length > 1 && (
            <span style={{ fontFamily: "Arial", fontSize: compact ? 10 : 11, color: "var(--ww-text-muted)",
                           textTransform: "lowercase" }}>
              {label.replace("usually runs", "mostly")}
            </span>
          )}
          {chipKeys.slice(0, 2).map((k, i) => (
            <Chip key={k} iko={k} compact={compact} />
          ))}
        </div>
      )}
    </div>
  );
}

function Chip({ iko: k, compact }) {
  const t = IKO[k];
  if (!t) return null;
  const plain = plainEnglish(k);
  return (
    <span data-testid={`iko-mix-chip-${k}`}
          style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: compact ? "1px 8px" : "3px 10px", borderRadius: 999,
            background: "var(--ww-sunken)",
            border: `1px solid var(--iko-title-${k}, var(--ww-line-strong))`,
            color: `var(--iko-title-${k}, var(--ww-text))`,
            fontFamily: "Arial", fontSize: compact ? 10 : 11,
            textTransform: "none", whiteSpace: "nowrap",
          }}>
      {plain}
    </span>
  );
}

function plainEnglish(k) {
  // Msg 232 · Vania: iko names, not "Party / Nightlife".
  return ({
    sherehe: "sherehe",
    polite:  "polite",
    michezo: "michezo",
    kazi:    "kazi",
  }[k] || k);
}
